










































































































import { ROUTE_PROJECT_DEFAULT } from '@/areas/projects/projects-routes';
import { ROUTE_SETTINGS } from '@/areas/settings/settings-routes';
import OtCheckboxGroup, {
  CheckboxGroupItem,
  CheckboxGroupSet,
} from '@/components/global/checkbox-group/ot-checkbox-group.vue';
import OtButton, { OtBtnSize, OtBtnStyle, OtBtnType } from '@/components/global/ot-button.vue';
import OtTag, { TagStatus } from '@/components/global/ot-tag.vue';
import OtTableHeader from '@/components/global/table/ot-table-header.vue';
import { IColumnData, IExtendedColumnData } from '@/components/global/table/ot-table-models';
import { getPaginatedBodyData, handleHeaderClick, sortTableData } from '@/components/global/table/ot-table-utils';
import OtTable from '@/components/global/table/ot-table.vue';
import OtApi, { executeApi } from '@/services/api.service';
import { PostUpdateDocumentAssociationsRequestModel } from '@/services/generated/api';
import { OtUserAction } from '@/types/action-enums';
import { OtStatusType } from '@/types/status-enums';
import { formatDate } from '@/utils/date-utils';
import { Component, Prop, Vue } from 'vue-property-decorator';
import { RawLocation } from 'vue-router';
import { DocumentDetails, DocumentVersion } from '@/components/documents/document-models';

type AssociationType = 'Organisation' | 'Project' | 'Contract' | 'Workflow';
const associationTypeOrder: { [key in AssociationType]: number } = {
  Organisation: 0,
  Project: 1,
  Contract: 2,
  Workflow: 3,
};

type WorkflowAssociationSegmentDetails = {
  segmentName: string;
  segmentShortName: string;
};

type BaseAssociationItem = {
  key: string;
  text: string;
  to: RawLocation;
  createdDateUtc: Date;
  status: TagStatus;
  availableActions: OtUserAction[];
  associatedGid: string;
  subText?: string;
  // to consider: array of actions?
};
type NormalAssociationItem = BaseAssociationItem & {
  associationType: 'Organisation' | 'Project' | 'Contract';
};
type WorkflowAssociationItem = BaseAssociationItem & {
  associationType: 'Workflow';
  details: WorkflowAssociationSegmentDetails;
};
type AssociationItem = NormalAssociationItem | WorkflowAssociationItem;

function sortAssociationByText(a: AssociationItem, b: AssociationItem) {
  return a.text.localeCompare(b.text);
}
function sortAssociationByType(a: AssociationItem, b: AssociationItem) {
  return associationTypeOrder[a.associationType] - associationTypeOrder[b.associationType];
}
function sortAssociationByCreatedDate(a: AssociationItem, b: AssociationItem) {
  return a.createdDateUtc.getTime() - b.createdDateUtc.getTime();
}

// NO. Do not do this. If you put it in @router, like the other route names, router gets imported in the wrong order and shit goes to hell in a handbasket.
// import { ROUTE_GOTO } from '@/router';
// instead, we'll put it here for now. Later, a centralised list of route names, and perhaps even some factories to make the @#$%ing RawLocations
const ROUTE_GOTO = 'GoTo';

interface IMenuOption {
  key: string;
  label: string;
  onClick: (association: AssociationItem) => void;
}

@Component({
  components: {
    OtTable,
    OtTableHeader,
    OtCheckboxGroup,
    OtButton,
    OtTag,
  },
})
export default class OtDocumentAssociationsList extends Vue {
  // * PROPS - not all of these are needed, so far this is used in the settings area, they'll be filled in by the router-view
  @Prop() private document?: DocumentDetails;
  @Prop() private version?: DocumentVersion;
  @Prop() private orgGid?: string;
  @Prop() private documentGid?: string; // woo, document AND the gid, as separate props, go team! And by Team, I mean me. Dan was foolish.
  @Prop() private locationDescription?: string;

  // * REFS

  // * DATA
  private api = new OtApi();
  private buttonSize = OtBtnSize.Tiny;
  private buttonStyle = OtBtnStyle.Outline;
  private buttonType = OtBtnType.Icon;
  private page = 1;
  private searchText = '';

  private tableColumns: IExtendedColumnData<AssociationItem>[] = [
    {
      index: 0,
      label: 'Name',
      key: 'name',
      isActive: true,
      ascending: true,
      sortable: true,
      sortFunction: sortAssociationByText,
    },
    {
      index: 1,
      label: 'Type',
      key: 'type',
      isActive: false,
      ascending: false,
      sortable: true,
      sortFunction: sortAssociationByType,
    },
    {
      index: 2,
      label: 'Date of Association',
      key: 'dateOfAssociation',
      isActive: false,
      ascending: false,
      sortable: true,
      sortFunction: sortAssociationByCreatedDate,
    },
    {
      index: 3,
      label: 'Status',
      key: 'status',
      isActive: false,
      ascending: false,
      sortable: false,
    },
    {
      index: 4,
      label: '',
      key: 'actionMenu',
      isActive: false,
      ascending: false,
      sortable: false,
    },
  ];
  private activeColumn = this.tableColumns[0];

  private rowLoadingStates: { [key: string]: boolean } = {};

  // * COMPUTED
  private get allAssociations(): AssociationItem[] {
    if (!this.document && this.version) {
      return [];
    }
    let associations: AssociationItem[] = [];
    if (this.document?.organisationAssociations) {
      associations = associations.concat(
        this.document.organisationAssociations.map(x => ({
          key: x.gid,
          text: x.organisationName ?? '',
          to: {
            name: ROUTE_SETTINGS,
            params: { orgId: x.organisationGid },
          },
          associationType: 'Organisation',
          createdDateUtc: x.createdDateUtc,
          status: { type: OtStatusType.Organisation, status: x.status },
          availableActions: x.availableActions,
          associatedGid: x.organisationGid,
        })),
      );
    }
    if (this.document?.projectAssociations) {
      associations = associations.concat(
        this.document.projectAssociations.map(x => ({
          key: x.gid,
          text: x.projectName ?? '',
          to: {
            name: ROUTE_PROJECT_DEFAULT,
            params: { projectGid: x.projectGid },
          },
          associationType: 'Project',
          createdDateUtc: x.createdDateUtc,
          status: { type: OtStatusType.Project, status: x.status },
          availableActions: x.availableActions,
          associatedGid: x.projectGid,
        })),
      );
    }
    if (this.document?.contractAssociations) {
      associations = associations.concat(
        this.document.contractAssociations.map(x => ({
          key: x.gid,
          text: x.contractName ?? '',
          to: {
            name: ROUTE_GOTO,
            query: { contractGid: x.contractGid },
          },
          associationType: 'Contract',
          createdDateUtc: x.createdDateUtc,
          status: { type: OtStatusType.Contract, status: x.status },
          availableActions: x.availableActions,
          associatedGid: x.contractGid,
        })),
      );
    }
    if (this.version?.segmentInstanceAssociations) {
      associations = associations.concat(
        this.version.segmentInstanceAssociations.map(x => ({
          key: x.gid,
          text: `${x.segmentInstanceCode ?? ''} - ${x.segmentName}`,
          to: {
            name: ROUTE_GOTO,
            query: { workflowGid: x.segmentInstanceGid },
          },
          associationType: 'Workflow',
          createdDateUtc: x.createdDateUtc,
          status: { type: OtStatusType.Segment, status: x.status },
          details: {
            segmentName: x.segmentName,
            segmentShortName: x.segmentShortName,
          },
          availableActions: x.availableActions,
          associatedGid: x.segmentInstanceGid,
          subText: `[${x.associationType.name}]`,
        })),
      );
    }
    return associations;
  }
  private get sortedBodyData() {
    return sortTableData(this.tableColumns, this.activeColumn, this.allAssociations);
  }

  private get filteredBodyData() {
    const types = new Set(this.selectedAssociationTypeFilters.map(s => s.value));
    const workflowTypes = this.selectedWorkflowTypeFilters.map(s => s.value);

    // a future enhancement could conditionally execute this, if we get thousands of associations to search
    // for now, we'll just go dumb. A blank string matches everything (that's how .test works)
    const textMatcher = new RegExp(this.searchText, 'i');

    const typeMatcher = (row: AssociationItem) => {
      // workflows are handled by another test. Not this one
      if (row.associationType === 'Workflow') {
        return true;
      }
      if (types.size === 0 && workflowTypes.length === 0) {
        // they don't care about anything. It's a match
        return true;
      }
      if (types.size === 0 && workflowTypes.length !== 0) {
        // they've picked at least one workflow type
        // but have not picked org/contract/project
        // this isn't a workflow
        // therefore, this cannot match
        return false;
      }
      // by now, we know they've selected at least org/project/contract, we can just check if the type is in the array and go home happy
      return types.has(row.associationType);
    };

    const workflowTypeMatcher = (row: AssociationItem) => {
      // this test only hanldes workflows
      // non workflow rows are handled in other tests
      if (row.associationType !== 'Workflow') {
        return true;
      }
      if (workflowTypes.length === 0 && types.size === 0) {
        // they've not picked anything. It's a match
        return true;
      }
      if (workflowTypes.length === 0 && types.size !== 0) {
        // they've picked org/contract/project
        // but haven't picked any workflow types
        // this is a workflow
        // therefore, this cannot match
        return false;
      }

      // by now, we know that they've selected at least one workflow type, we can check if this row is the right type
      // a future improvement would build a hash of the two strings, a Set of the selections, something, for quicker look up
      // this'll do, if they have thousands of selections, and they want to filter to one segment type, it might be slow
      return workflowTypes.some(
        selection =>
          selection.segmentName === row.details.segmentName &&
          selection.segmentShortName === row.details.segmentShortName,
      );
    };

    return this.sortedBodyData.filter(row => {
      return textMatcher.test(row.text) && typeMatcher(row) && workflowTypeMatcher(row);
    });
  }
  private get paginatedBodyData() {
    return getPaginatedBodyData(this.filteredBodyData);
  }

  private get currentBodyData() {
    return this.paginatedBodyData[this.page - 1];
  }

  private get pageCount() {
    let currentMaxPage = this.paginatedBodyData.length;
    if (this.page > currentMaxPage && currentMaxPage >= 1) {
      currentMaxPage = this.paginatedBodyData.length - 1;
      this.page = currentMaxPage;
    }
    return currentMaxPage;
  }

  private get workflowTypeFilters() {
    // Hmm. See. The idea I had here was "make the API call to get the list of workflow types""
    // But, workflow types are based on the contract. We can get the full list of contracts easily enough
    // but then we'd need to make a call to get the workflow types for each contract, and that's a lot of calls
    // We could hardcode the list of workflow types, but that'd be a pain to keep in sync
    // So, I guess, we do it based on the data we have. Which I hate. But, it'll do for now.
    if (!this.version) {
      return [];
    }
    const distinctSegments = new Map(
      this.version.segmentInstanceAssociations.map(x => [
        `${x.segmentShortName}:${x.segmentName}`,
        { segmentShortName: x.segmentShortName, segmentName: x.segmentName },
      ]),
    );
    return [
      new CheckboxGroupSet<AssociationType, WorkflowAssociationSegmentDetails>(
        'Workflow',
        Array.from(distinctSegments.values()).map(x => new CheckboxGroupItem(x.segmentName, x)),
      ),
    ];
  }
  private selectedWorkflowTypeFilters: CheckboxGroupItem<WorkflowAssociationSegmentDetails>[] = [];
  private get hasWorkflowAssociations() {
    return Boolean(this.version?.segmentInstanceAssociations.length);
  }

  private associationTypeFilters: CheckboxGroupItem<AssociationType>[] = [
    new CheckboxGroupItem('Organisation', 'Organisation'),
    new CheckboxGroupItem('Project', 'Project'),
    new CheckboxGroupItem('Contract', 'Contract'),
  ];
  private selectedAssociationTypeFilters: CheckboxGroupItem<AssociationType>[] = [];

  // * WATCHERS

  // * METHODS
  private setRowLoadingStates() {
    this.rowLoadingStates = this.allAssociations.reduce((a, x) => ({ ...a, [x.key]: false }), {});
  }
  private updateCurrentPage(value: number) {
    this.page = value;
  }
  private headerClick(header: IColumnData) {
    const handledHeaders = handleHeaderClick(this.tableColumns, this.activeColumn, header);
    this.tableColumns = handledHeaders.tableColumns;
    this.activeColumn = handledHeaders.activeColumn;
  }
  private formatDate(date: Date | null): string {
    if (date) {
      return formatDate(date);
    }
    return '';
  }

  private createRemoveDocumentAssocationMenuItem(association: AssociationItem): IMenuOption {
    return {
      key: 'remove',
      label: `Remove from ${association.associationType}`,
      onClick: this.removeDocumentAssociation,
    };
  }

  private getAssociationOptionMenuItems(association: AssociationItem): IMenuOption[] {
    const results: IMenuOption[] = [];
    // For reasons, the server returns "yeah, you can remove it" for workflows
    // But, we DO NOT want to remove from workflows
    // They can only do that if they reopen the workflow
    // If and when the server returns more correct data, we can remove this check
    // There is actually one semi legit case for removing external to the workflow - removing a supporting document that's been added to a closed workflow
    // That is one that they probably need to be able to do. But it wasn't specc'd, and will be a 23 hour discussion, so Dan is ignoring it for now.
    // In the servers defence, this was a bit up in flux, and revisiting the available actions was not high on the priority list
    if (association.associationType !== 'Workflow' && association.availableActions.includes(OtUserAction.Remove)) {
      results.push(this.createRemoveDocumentAssocationMenuItem(association));
    }
    return results;
  }

  private async removeDocumentAssociation(association: AssociationItem) {
    const documentGid = this.document?.gid;
    if (!documentGid) {
      console.error(
        'ot-document-associations-list -> removeDocumentAssociation -> somehow the document has no gid. This is not good. Bailing',
      );
      return;
    }
    this.rowLoadingStates[association.key] = true;
    console.log('ot-document-associations-list -> removeDocumentAssociation -> association:  ', { association });
    if (association.associationType === 'Workflow') {
      // workflows are a different call - the segment instance update documents call
      alert(
        'Removing from a workflow is not yet supported. If you see this message, tell Dan. This needs to be the Claim Documents call, not the Document Associations call',
      );
    } else {
      const request = new PostUpdateDocumentAssociationsRequestModel();
      if (association.associationType === 'Organisation') {
        request.removeOrganisationAssociations = [association.associatedGid];
      }
      if (association.associationType === 'Project') {
        request.removeProjectAssociations = [association.associatedGid];
      }
      if (association.associationType === 'Contract') {
        request.removeContractAssociations = [association.associatedGid];
      }
      await executeApi(
        () => this.api.documents().postUpdateDocumentAssociations(documentGid, request),
        'Update Document Associations',
      );
    }
    this.rowLoadingStates[association.key] = false;
    this.$emit('reloadDocument');
  }

  // * LIFECYCLE
  private created() {
    console.log('ot-document-associations-list -> created ');
    // This initial dictionary population is required otherwise the
    // value doesn't always bind correctly and the loading could get stuck
    this.setRowLoadingStates();
  }
}
